import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleRedirectResponse, msalLogout } from '../lib/msal.js';
import { setKey, setUser } from '../lib/auth.jsx';

// /auth/callback — receives the redirect from Microsoft Entra External ID
// after the user signs up or signs in. Pulls the id token, hands it to the
// backend's /api/auth/check (which validates the JWT via lib/b2c.js) and
// stores the resulting Atlas user.
export default function AuthCallback() {
  const nav = useNavigate();
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      const result = await handleRedirectResponse();
      if (!result || !result.idToken) {
        setErr('Microsoft sign-in did not return a token. Try again.');
        return;
      }
      // Hand the JWT to the backend in the same way every other API call does.
      try {
        // Store JWT under as_key so existing X-Admin-Key middleware picks it up
        setKey(result.idToken);
        const r = await fetch('/api/auth/check', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${result.idToken}`,
          },
          body: JSON.stringify({}),
        });
        const data = await r.json();
        if (!r.ok || !data.user) {
          // Likely: signed in to Microsoft but no MAPTRIUM account exists yet.
          // Sign them out of MSAL so they can try a different identity.
          await msalLogout();
          setErr(data?.detail || data?.error || 'No MAPTRIUM account is linked to this Microsoft identity. Ask your admin to create one for this email.');
          return;
        }
        setUser(data.user);
        nav(data.user.role === 'admin' ? '/admin' : '/dashboard', { replace: true });
      } catch (e) {
        setErr(e.message || 'Sign-in failed');
      }
    })();
  }, [nav]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy via-navy-700 to-navy-800 flex items-center justify-center p-6">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full">
        <h1 className="text-xl font-bold text-navy mb-2">Signing you in…</h1>
        {err ? (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 mt-4">
            {err}
            <div className="mt-3">
              <a href="/login" className="btn btn-sm btn-primary">Back to sign-in</a>
            </div>
          </div>
        ) : (
          <p className="text-sm text-ink-muted">Validating your Microsoft credentials…</p>
        )}
      </div>
    </div>
  );
}
